import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Timeline } from '../components/about/Timeline';
import { ServicesAccordion } from '../components/about/ServicesAccordion';
import { ProjectRequestWizard } from '../components/about/ProjectRequestWizard';
import { Sparkles, Target, Lightbulb } from 'lucide-react';
export function About() {
  return <>
      <Helmet data-id="element-1309">
        <title data-id="element-1310">About Bright Pixxel Studios | UK Web Design Since 2018</title>
        <meta name="description" content="We're a UK family business delivering quality, transparency and innovation. 500+ websites built, SEO-first approach." data-id="element-1311" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-16 md:pt-24 pb-16 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1312">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1313">
          <div className="flex flex-col md:flex-row items-center md:space-x-12" data-id="element-1314">
            <div className="md:w-1/2 mb-8 md:mb-0" data-id="element-1315">
              <motion.span initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5
            }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1316">
                About Us
              </motion.span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-3xl md:text-5xl font-bold mb-6" data-id="element-1317">
                We Create Websites That Deliver Results
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.2
            }} className="text-xl text-gray-700 dark:text-gray-200 mb-6 font-medium" data-id="element-1318">
                Since 2018 we've built over 500 websites for UK businesses of
                all sizes.
              </motion.p>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="text-lg text-gray-600 dark:text-gray-300 mb-6" data-id="element-1319">
                Bright Pixxel Studios is a UK-based family business with an
                SEO-first approach that focuses on delivering measurable
                business results. We've helped businesses across various
                industries establish their online presence and grow their
                customer base.
              </motion.p>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1320">
                Our team combines technical expertise with creative design to
                create websites that not only look beautiful but also perform
                exceptionally well in search engines and convert visitors into
                customers.
              </motion.p>
            </div>
            <div className="md:w-1/2" data-id="element-1321">
              <motion.div initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5,
              delay: 0.5
            }} className="relative rounded-xl overflow-hidden shadow-xl" data-id="element-1322">
                <img src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80" alt="Bright Pixxel team collaboration" className="w-full h-auto" data-id="element-1323" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" data-id="element-1324">
                  <div className="absolute bottom-4 left-4 right-4" data-id="element-1325">
                    <div className="flex items-center" data-id="element-1326">
                      <div className="w-2 h-2 rounded-full bg-green-500 mr-2" data-id="element-1327"></div>
                      <p className="text-white text-sm font-medium" data-id="element-1328">
                        UK-based team dedicated to your success
                      </p>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>
      {/* Timeline Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-1329">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1330">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1331">
            <motion.span initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1332">
              Our Journey
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1333">
              The Bright Pixxel Story
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1334">
              From our humble beginnings to becoming a trusted web design
              partner for businesses across the UK, our journey has been defined
              by continuous growth and innovation.
            </motion.p>
          </div>
          <Timeline data-id="element-1335" />
        </div>
      </section>
      {/* Values Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1336">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1337">
          <div className="max-w-3xl mx-auto text-center mb-16" data-id="element-1338">
            <motion.span initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} viewport={{
            once: true
          }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1339">
              Our Values
            </motion.span>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1340">
              What Drives Us
            </motion.h2>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1341">
              Our core values shape everything we do, from how we design
              websites to how we interact with our clients.
            </motion.p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-1342">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="bg-white dark:bg-[#1E1E1E] p-8 rounded-xl shadow-md" data-id="element-1343">
              <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/20 rounded-lg flex items-center justify-center mb-4" data-id="element-1344">
                <Sparkles size={24} className="text-purple-600" data-id="element-1345" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-1346">Quality</h3>
              <p className="text-gray-600 dark:text-gray-300" data-id="element-1347">
                We never compromise on quality. Every website we build is
                crafted with attention to detail, using best practices for
                performance, security, and user experience.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="bg-white dark:bg-[#1E1E1E] p-8 rounded-xl shadow-md" data-id="element-1348">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center mb-4" data-id="element-1349">
                <Target size={24} className="text-blue-600" data-id="element-1350" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-1351">Transparency</h3>
              <p className="text-gray-600 dark:text-gray-300" data-id="element-1352">
                We believe in clear communication and transparent pricing.
                You'll always know what you're paying for and what to expect
                throughout the project lifecycle.
              </p>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="bg-white dark:bg-[#1E1E1E] p-8 rounded-xl shadow-md" data-id="element-1353">
              <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center mb-4" data-id="element-1354">
                <Lightbulb size={24} className="text-green-600" data-id="element-1355" />
              </div>
              <h3 className="text-xl font-semibold mb-3" data-id="element-1356">Innovation</h3>
              <p className="text-gray-600 dark:text-gray-300" data-id="element-1357">
                We stay at the forefront of web design trends and technologies,
                continuously learning and evolving to provide innovative
                solutions for our clients' challenges.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      {/* Services Accordion Section */}
      <section className="py-16 md:py-24 bg-white dark:bg-[#1E1E1E]" data-id="element-1358">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1359">
          <div className="max-w-3xl mx-auto" data-id="element-1360">
            <div className="text-center mb-16" data-id="element-1361">
              <motion.span initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1362">
                Our Services
              </motion.span>
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1363">
                What We Offer
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1364">
                From standard websites to e-commerce solutions and ongoing
                maintenance, we provide a comprehensive range of digital
                services.
              </motion.p>
            </div>
            <ServicesAccordion data-id="element-1365" />
          </div>
        </div>
      </section>
      {/* Project Request Wizard Section */}
      <section className="py-16 md:py-24 bg-[#F9FAFB] dark:bg-[#121212]" data-id="element-1366">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8" data-id="element-1367">
          <div className="max-w-4xl mx-auto" data-id="element-1368">
            <div className="text-center mb-16" data-id="element-1369">
              <motion.span initial={{
              opacity: 0
            }} whileInView={{
              opacity: 1
            }} viewport={{
              once: true
            }} className="inline-block px-3 py-1 bg-[#3AA6F9]/10 dark:bg-[#3AA6F9]/20 text-[#3AA6F9] rounded-full text-sm font-medium mb-4" data-id="element-1370">
                Start Your Project
              </motion.span>
              <motion.h2 initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5
            }} className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1371">
                Tell Us About Your Project
              </motion.h2>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-lg text-gray-600 dark:text-gray-300" data-id="element-1372">
                Use our project request wizard to help us understand your needs
                and provide you with a tailored solution.
              </motion.p>
            </div>
            <ProjectRequestWizard data-id="element-1373" />
          </div>
        </div>
      </section>
    </>;
}